import { Button as MaterialButton } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import arrayMutators from 'final-form-arrays';
import setFieldData from 'final-form-set-field-data';
import { useId, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';

import { notify } from '@/actions/ui';
import Button from '@/components/Button';
import FAIcon from '@/components/Icon/FAIcon';
import LinearProgress from '@/components/LinearProgress';
import MediaQuery from '@/components/MediaQuery';
import Queries from '@/components/Queries';
import ProfileTitle from '@/profile/components/ProfileTitle';
import { SCREEN_XS } from '@/theme/screens';
import { normalise } from '@/utils';

const useStyles = makeStyles({
  paper: {
    padding: 24,
  },
  paperWidthSm: {
    maxWidth: 640,
  },
  paperFullScreen: {
    boxSizing: 'border-box',
  },
  section: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginTop: '0px',
    marginBottom: '0px',
  },
  question: {
    fontSize: '6px',
    fontWeight: 'bold',
    marginTop: '0px',
    marginBottom: '0px',
  },

  actions: {
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  done: {
    display: 'flex',
    alignItems: 'center',
  },
  disabledInput: {
    '& .MuiInputBase-root.Mui-disabled': {
      color: 'var(--onSurface)',
    },
  },
  disabledCheckbox: {
    '&.Mui-disabled': {
      color: 'rgba(0,100,110,0.5)',
    },
  },
  disabledLabel: {
    '&.Mui-disabled': {
      color: 'var(--onSurface)',
    },
  },
  hiddenSection: {
    marginTop: '12px',
  },
  hiddenToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: 'none',
    padding: '4px 0',
    cursor: 'pointer',
    color: 'var(--onSurfaceMuted)',
    font: 'inherit',
  },
  hiddenAnswers: {
    opacity: 0.55,
    pointerEvents: 'none',
  },
});

function ProgressDialog({
  expertRequest,
  candidate,
  editable,
  progressOpen,
  onClose,

  // Final Form
  form,

  submitting,
  handleSubmit,
  values,
}: any) {
  // Hooks
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const s = useStyles();

  const [showHiddenQuestions, setShowHiddenQuestions] = useState(false);
  const [showHiddenQualifications, setShowHiddenQualifications] = useState(false);
  const hiddenQuestionsPanelId = useId();
  const hiddenQualificationsPanelId = useId();

  const visibleQuestions = (expertRequest.questions ?? []).filter((q: any) => !q.hidden);
  const hiddenQuestions = (expertRequest.questions ?? []).filter((q: any) => q.hidden);
  const visibleQualifications = (expertRequest.qualifications ?? []).filter((q: any) => !q.hidden);
  const hiddenQualifications = (expertRequest.qualifications ?? []).filter((q: any) => q.hidden);

  const onCloseWithReset = () => {
    form.reset();
    onClose();
  };
  return (
    <Dialog
      open={progressOpen}
      scroll="body"
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      classes={{
        paper: s.paper,
        paperWidthSm: s.paperWidthSm,
        paperFullScreen: s.paperFullScreen,
      }}
    >
      <ProfileTitle profile={candidate.profile} />

      <form onSubmit={handleSubmit}>
        {values.questions?.length > 0 && (
          <div className={s.section}>
            <p className={s.sectionTitle}>Able to answer these questions</p>
            <FieldArray
              component={Queries}
              name="questions"
              key="questions"
              queries={visibleQuestions}
              disabled={!editable}
              field={Field}
              hideIndex
            />
            {hiddenQuestions.length > 0 && (
              <div className={s.hiddenSection}>
                <button
                  type="button"
                  className={s.hiddenToggle}
                  aria-expanded={showHiddenQuestions}
                  aria-controls={hiddenQuestionsPanelId}
                  onClick={() => setShowHiddenQuestions((v) => !v)}
                >
                  <FAIcon icon={showHiddenQuestions ? 'chevron-down' : 'chevron-right'} size={12} />
                  <span>Hidden questions</span>
                </button>
                <Collapse in={showHiddenQuestions} unmountOnExit>
                  <div className={s.hiddenAnswers} id={hiddenQuestionsPanelId}>
                    <FieldArray
                      component={Queries}
                      name="questions"
                      key="hidden-questions"
                      queries={hiddenQuestions}
                      disabled
                      field={Field}
                      hideIndex
                    />
                  </div>
                </Collapse>
              </div>
            )}
          </div>
        )}

        {values.qualifications?.length > 0 && (
          <div className={s.section}>
            <p className={s.sectionTitle}>Qualifications</p>
            <FieldArray
              component={Queries}
              name="qualifications"
              key="qualifications"
              queries={visibleQualifications}
              disabled={!editable}
              field={Field}
              hideIndex
            />
            {hiddenQualifications.length > 0 && (
              <div className={s.hiddenSection}>
                <button
                  type="button"
                  className={s.hiddenToggle}
                  aria-expanded={showHiddenQualifications}
                  aria-controls={hiddenQualificationsPanelId}
                  onClick={() => setShowHiddenQualifications((v) => !v)}
                >
                  <FAIcon
                    icon={showHiddenQualifications ? 'chevron-down' : 'chevron-right'}
                    size={12}
                  />
                  <span>Hidden qualifications</span>
                </button>
                <Collapse in={showHiddenQualifications} unmountOnExit>
                  <div className={s.hiddenAnswers} id={hiddenQualificationsPanelId}>
                    <FieldArray
                      component={Queries}
                      name="qualifications"
                      key="hidden-qualifications"
                      queries={hiddenQualifications}
                      disabled
                      field={Field}
                      hideIndex
                    />
                  </div>
                </Collapse>
              </div>
            )}
          </div>
        )}

        <div className={s.actions}>
          <div>
            <MaterialButton onClick={onCloseWithReset}>
              {editable ? 'Cancel' : 'Close'}
            </MaterialButton>
          </div>

          {editable && (
            <div className={s.done}>
              <Button disabled={submitting} type="submit" size="medium">
                Save
              </Button>
            </div>
          )}
        </div>
      </form>
    </Dialog>
  );
}

function MatchProgress({ editable, onSubmit, notify, expertRequest, candidate }: any) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);

  const handleProgressClick = (event: any) => {
    event.preventDefault();

    setProgressOpen(true);
  };

  const handleProgressClose = () => {
    setProgressOpen(false);
    // wait for animation to complete
    setTimeout(() => setIsSubmitting(false), 500);
  };

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const qualificationResponses = values.qualifications.map((q: any) => ({
        query_id: q.query.id,
        ...(q.query.response_type === 'yes_no' && { can_answer: q.can_answer }),

        ...(q.query.response_type === 'free_form' && {
          text_response: q.text_response,
        }),
      }));
      const questionAnswers = values.questions.map((q: any) => ({
        query_id: q.query.id,
        ...(q.query.response_type === 'yes_no' && { can_answer: q.can_answer }),

        ...(q.query.response_type === 'free_form' && {
          text_response: q.text_response,
        }),
      }));
      const candidateValues = {
        id: values.id,
        qualification_responses: qualificationResponses,
        question_answers: questionAnswers,
      };
      await onSubmit(candidateValues);
      notify('Expert answers updated.', 'success');
      handleProgressClose();
    } catch (err) {
      notify('Error when updating expert answers.', 'error');
      setIsSubmitting(false);
      throw err;
    }
  };

  const { questions, qualifications } = expertRequest;

  // Hidden questions are view-only history; they don't count toward the match progress.
  const visibleQuestions = (questions ?? []).filter((q: any) => !q.hidden);
  const visibleQualifications = (qualifications ?? []).filter((q: any) => !q.hidden);

  const { qualification_responses: qualificationResponses, question_answers: questionResponses } =
    candidate;

  const linearProgressValue = useMemo(() => {
    // TODO: Max is going to check with RM a better solution for this component
    const validQuestionResponses =
      questionResponses?.filter(
        (a: any) =>
          (a.can_answer === true || a.text_response !== '') &&
          visibleQuestions.some((q: any) => q.id === a.query?.id)
      ) || [];
    const validQualificationResponses =
      qualificationResponses?.filter(
        (a: any) =>
          (a.can_answer === true || a.text_response !== '') &&
          visibleQualifications.some((q: any) => q.id === a.query?.id)
      ) || [];

    return normalise(
      validQuestionResponses.length + validQualificationResponses.length,
      visibleQuestions.length + visibleQualifications.length
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertRequest, candidate]);

  const totalQuestions = useMemo(() => {
    const unrespondedQuestions =
      questionResponses &&
      questions
        .filter((q: any) => !questionResponses.some((a: any) => a.query?.id === q.id))
        .map((q: any) => ({
          query: q,
          text_reponse: '',
        }));

    return [...questionResponses].filter((r) => r.query).concat(unrespondedQuestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertRequest, candidate]);

  const totalQualifications = useMemo(() => {
    const unrespondedQualifications =
      qualificationResponses &&
      qualifications
        .filter((q: any) => !qualificationResponses.some((a: any) => a.query?.id === q.id))
        .map((q: any) => ({
          query: q,
          text_reponse: '',
        }));

    return [...qualificationResponses].filter((r) => r.query).concat(unrespondedQualifications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertRequest, candidate]);

  const initialValues = {
    id: candidate.id,
    qualifications: totalQualifications,
    questions: totalQuestions,
  };
  return (
    <div>
      <LinearProgress
        variant="determinate"
        value={linearProgressValue}
        style={{
          width: 100,
          borderRadius: 5,
          height: 8,
          marginRight: 15,
          cursor: 'pointer',
        }}
        onClick={handleProgressClick}
      />
      <MediaQuery maxWidth={SCREEN_XS}>
        {() => (
          <Form
            // Final Form
            initialValues={initialValues}
            onSubmit={handleSubmit}
            mutators={{ setFieldData, ...arrayMutators }}
            component={ProgressDialog}
            // Only re-render if these parts of the form state change
            subscription={{
              submitting: true,
              values: true,
            }}
            // ProgressDialog props
            expertRequest={expertRequest}
            candidate={candidate}
            editable={editable}
            progressOpen={progressOpen}
            onClose={handleProgressClose}
          />
        )}
      </MediaQuery>
    </div>
  );
}

export default connect(undefined, {
  notify,
})(MatchProgress);
